import { useNavigate } from 'react-router-dom';
import { useTabSystem } from '../context/TabContext';
import { useAuth } from '../context/AuthContext';
import { X, Maximize2, Minimize2, RotateCw, Pin } from 'lucide-react';

const TabBar = ({ sidebarOpen, customSidebarWidth, isStatic }) => {
  const { tabs, activeTabId, switchTab, closeTab, refreshTab, togglePinTab, isFullscreen, toggleFullscreen, isTabEnabled } = useTabSystem();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tabEnabled = isTabEnabled(user?.role || '');
  const userId = user?.email || user?._id || '';

  if (!tabEnabled || tabs.length === 0) return null;

  const sidebarWidth = customSidebarWidth || (sidebarOpen ? '256px' : '64px');

  const handleTabClick = (tab) => {
    switchTab(tab.id);
    navigate(tab.path, { replace: true });
  };

  const handleClose = (e, tabId) => {
    e.stopPropagation();
    const idx = tabs.findIndex(t => t.id === tabId);
    const remaining = tabs.filter(t => t.id !== tabId);
    closeTab(tabId);
    if (tabId === activeTabId && remaining.length > 0) {
      const newIdx = Math.min(idx, remaining.length - 1);
      navigate(remaining[newIdx].path, { replace: true });
    }
  };

  const handleRefresh = (e, tabId) => {
    e.stopPropagation();
    refreshTab(tabId);
  };

  const handlePin = (e, tabId) => {
    e.stopPropagation();
    togglePinTab(tabId, userId);
  };

  return (
    <div
      className={`${isStatic ? 'relative w-full' : 'fixed top-0 right-0'} z-40 hidden lg:flex items-center h-[38px] bg-[#1e2128] border-b border-[#2d3139] select-none`}
      style={isStatic ? {} : { left: sidebarWidth, transition: 'left 0.3s' }}
      data-testid="tab-bar"
    >
      <div className="flex-1 flex items-end h-full overflow-x-auto scrollbar-hide px-1 gap-[2px]">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const isPinned = tab.pinned;
          return (
            <div
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => handleTabClick(tab)}
              className={`group flex items-center gap-1 px-3 h-[34px] mt-auto cursor-pointer text-[13px] rounded-t-lg transition-all min-w-0 max-w-[220px] border-t-2 ${
                isActive
                  ? 'bg-white text-slate-900 font-semibold border-t-blue-500 shadow-sm'
                  : 'bg-[#25282e] text-[#8b919a] hover:text-white hover:bg-[#2d3139] border-t-transparent font-medium'
              }`}
            >
              {/* Pin ikonu - sabitlenmişse her zaman görünür */}
              {isPinned && (
                <Pin size={10} className={`flex-shrink-0 -rotate-45 ${isActive ? 'text-blue-500' : 'text-amber-500'}`} />
              )}
              <span className="truncate leading-none tracking-wide">{tab.label}</span>
              <div className="flex items-center gap-0.5 ml-auto flex-shrink-0">
                {/* Pin toggle */}
                <button
                  data-testid={`tab-pin-${tab.id}`}
                  onClick={(e) => handlePin(e, tab.id)}
                  className={`p-0.5 rounded transition-all ${
                    isPinned
                      ? (isActive ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' : 'text-amber-500/60 hover:text-amber-400 hover:bg-[#3d4149]')
                      : (isActive ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100' : 'text-[#5c6370] hover:text-white hover:bg-[#3d4149] opacity-0 group-hover:opacity-100')
                  }`}
                  title={isPinned ? 'Sabitlemeyi kaldir' : 'Sabitle (login\'de otomatik acilir)'}
                >
                  <Pin size={11} className={isPinned ? '-rotate-45' : ''} />
                </button>
                {/* Refresh */}
                <button
                  data-testid={`tab-refresh-${tab.id}`}
                  onClick={(e) => handleRefresh(e, tab.id)}
                  className={`p-0.5 rounded transition-all ${
                    isActive
                      ? 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-[#5c6370] hover:text-white hover:bg-[#3d4149] opacity-0 group-hover:opacity-100'
                  }`}
                  title="Yenile"
                >
                  <RotateCw size={11} />
                </button>
                {/* Close - sabitlenmiş tab'lar kapatılamaz */}
                {!isPinned && tabs.length > 1 && (
                  <button
                    data-testid={`tab-close-${tab.id}`}
                    onClick={(e) => handleClose(e, tab.id)}
                    className={`p-0.5 rounded transition-all ${
                      isActive
                        ? 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                        : 'text-[#5c6370] hover:text-white hover:bg-[#3d4149] opacity-0 group-hover:opacity-100'
                    }`}
                    title="Kapat"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fullscreen Toggle */}
      <button
        data-testid="fullscreen-toggle"
        onClick={toggleFullscreen}
        className={`px-3 h-full flex items-center transition-colors border-l border-[#2d3139] ${
          isFullscreen
            ? 'text-blue-400 hover:text-blue-300 bg-[#1e2128]'
            : 'text-[#5c6370] hover:text-white hover:bg-[#25282e]'
        }`}
        title={isFullscreen ? 'Tam ekrandan cik (ESC)' : 'Tam ekran'}
      >
        {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
      </button>
    </div>
  );
};

export default TabBar;
